import { ButtonProps } from '@mui/material'
import { Add } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { Device, Project, RuuviTag } from '../types'
import { AlertGroup, AlertTemplate } from '../types/Alerts'
import useDialog from '../hooks/useDialog'
import { usePolicy } from '../hooks'
import AlertRuleAddDialog from './AlertRuleAddDialog'
import ResponsiveButton from './ResponsiveButton'
import { AlertRuleDataGridRow } from './AlertRuleDataGrid'

export interface AlertRuleAddButtonProps extends Omit<
  ButtonProps,
  'onClick' | 'children' | 'endIcon'
> {
  alertRules: AlertGroup[]
  alertTemplates: AlertTemplate[]
  project?: Project
  device?: Device
  ruuviTag?: RuuviTag
  useRedux?: boolean
  beforeUpdate?: (input: AlertRuleDataGridRow[]) => void
  afterUpdate?: (alertRules: AlertGroup[]) => void
}

export default function AlertRuleAddButton({
  alertRules,
  alertTemplates,
  project,
  ruuviTag,
  device,
  variant = 'contained',
  color = 'primary',
  useRedux = true,
  beforeUpdate,
  afterUpdate,
  ...props
}: AlertRuleAddButtonProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation()

  /**
   * The policies.
   */
  const { canEditProjectAlertRules, canEditDeviceAlertRules, canEditRuuviTagAlertRules } =
    usePolicy()

  /**
   * Check if the user can edit alert rules.
   */
  const canEditAlertRules =
    (project && canEditProjectAlertRules(project)) ||
    (device && canEditDeviceAlertRules(device)) ||
    (ruuviTag && canEditRuuviTagAlertRules(ruuviTag))

  /**
   * The dialog state.
   */
  const { open, openDialog, closeDialog } = useDialog()

  /**
   * Handle the event before submit.
   */
  function handleBeforeSubmit(input: AlertRuleDataGridRow[]) {
    beforeUpdate?.(input)
  }

  /**
   * Handle the event after submit.
   */
  function handleAfterSubmit(alertRules: AlertGroup[]) {
    afterUpdate?.(alertRules)
  }

  /**
   * Handle the "close" event.
   */
  function handleClose() {
    closeDialog()
  }

  return (
    <>
      {canEditAlertRules && (
        <ResponsiveButton
          variant={variant}
          ButtonProps={{
            color
          }}
          icon={<Add />}
          endIcon={<Add />}
          label={t('addAlertRule')}
          {...props}
          onClick={openDialog}
        />
      )}

      <AlertRuleAddDialog
        open={open}
        alertRules={alertRules}
        alertTemplates={alertTemplates}
        project={project}
        device={device}
        ruuviTag={ruuviTag}
        useRedux={useRedux}
        beforeSubmit={handleBeforeSubmit}
        afterSubmit={handleAfterSubmit}
        onClose={handleClose}
      />
    </>
  )
}
